import { Directive, forwardRef, inject, Input, Provider, Type } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TableDataSource } from '@dice/app/directives/table-data-source';
import { HashMap } from '@dice/app/types/hash-map';
import {
  Column,
  DisplayValueFn,
  FilterPredicateFn,
  TableColumnFilter,
  TableFilterComponent,
} from '@dice/app/types/table.types';
import { get as _get, has as _has } from 'lodash';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';

export function provideFilterComponent<T>(component: Type<T>): Provider {
  return { provide: TableFilterComponent, useExisting: forwardRef(() => component) };
}

type TableFilterDataSource<T> = MatTableDataSource<T> | TableDataSource<T>;

@Directive({
  selector: 'mat-table[tableFilter]',
  standalone: false,
})
export class TableFilterDirective<T> {
  private tableFilterComponent = inject<TableFilterComponent<T>>(TableFilterComponent);

  @Input() set disableFilter(deactivated: boolean) {
    this.filteringDeactivated$.next(deactivated);
  }

  private readonly filteringDeactivated$ = new BehaviorSubject<boolean>(false);

  @Input() set dataSource(dataSource: MatTableDataSource<T> | TableDataSource<T>) {
    this.dataSource$.next(dataSource);
  }

  private readonly dataSource$ = new BehaviorSubject<TableFilterDataSource<T>>(null);

  @Input() set filterPredicate(filterPredicateFn: FilterPredicateFn<T>) {
    this.customFilterPredicate$.next(filterPredicateFn);
  }

  private readonly customFilterPredicate$ = new BehaviorSubject<FilterPredicateFn<T>>(null);

  @Input() set tableColumnFilters(tableColumnFilters: TableColumnFilter[]) {
    this.tableColumnFilters$.next(tableColumnFilters);
  }

  private readonly tableColumnFilters$ = new BehaviorSubject<TableColumnFilter[]>(null);

  private readonly EMPTY_SEARCH_FILTER: string = '¤EMPTY¤SEARCH¤FILTER¤';

  @Input() set searchFilter(searchFilter: string) {
    this.searchFilter$.next(searchFilter);
  }

  private readonly searchFilter$ = new BehaviorSubject<string>(null);

  private hasColumnsDefinition: boolean = false;

  private _valueAccessor: HashMap<DisplayValueFn<unknown>> = {};

  private readonly unsubscriber$: Subject<void> = new Subject<void>();

  constructor() {
    combineLatest([
      this.dataSource$,
      this.customFilterPredicate$,
      this.tableColumnFilters$,
      this.searchFilter$,
      this.filteringDeactivated$,
    ])
      .pipe(
        takeUntil(this.unsubscriber$),
        debounceTime(10),
        filter(([dataSource]) => !!dataSource),
        tap(([dataSource, customFilterPredicate, tableColumnFilters, searchFilter, filteringDeactivated]) => {
          const searchTerm = !!searchFilter || customFilterPredicate ? searchFilter : this.EMPTY_SEARCH_FILTER;
          this.readTableFilterComponentValues();
          this.applyFilterFn(dataSource, customFilterPredicate, tableColumnFilters, searchTerm, filteringDeactivated);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscriber$.next();
  }

  private readTableFilterComponentValues(): void {
    this.hasColumnsDefinition =
      _has(this, 'tableSortComponent.columns') && this.tableFilterComponent.columns.length > 0;
  }

  private applyFilterFn(
    dataSource: TableFilterDataSource<T>,
    customFilterPredicate: FilterPredicateFn<T>,
    tableColumnFilters: TableColumnFilter[],
    searchFilter: string,
    filteringDeactivated: boolean,
  ): void {
    if (dataSource) {
      dataSource.filterPredicate = this.getFilterFn(customFilterPredicate, tableColumnFilters, filteringDeactivated);
      dataSource.filter = searchFilter;
    }
  }

  private getFilterFn(
    customFilterPredicate: FilterPredicateFn<T>,
    tableColumnFilters: TableColumnFilter[],
    filteringDeactivated: boolean,
  ): (data: T, searchFilter: string) => boolean {
    return (data: T, searchFilter: string): boolean => {
      if (filteringDeactivated) {
        return true;
      }

      const searchFilterResult: boolean = this.getSearchResult(
        customFilterPredicate,
        data,
        searchFilter,
        tableColumnFilters,
      );

      if (searchFilterResult && tableColumnFilters && tableColumnFilters.length > 0) {
        return searchFilterResult && this.tableColumnFilterPredicate(data, tableColumnFilters);
      }

      return searchFilterResult;
    };
  }

  private tableColumnFilterPredicate(data: T, tableColumnFilters: TableColumnFilter[]): boolean {
    return tableColumnFilters.every(({ keyPath, filter }: TableColumnFilter) => {
      return !filter || _get(data, keyPath).trim().toLowerCase() === filter.toLowerCase();
    });
  }

  private getSearchResult(
    customFilterPredicate: FilterPredicateFn<T>,
    data: T,
    searchFilter: string,
    tableColumnFilters: TableColumnFilter[],
  ): boolean {
    if (customFilterPredicate) {
      return customFilterPredicate(data, searchFilter, tableColumnFilters);
    }
    if (searchFilter === this.EMPTY_SEARCH_FILTER) {
      return true;
    }
    if (this.hasColumnsDefinition) {
      return this.defaultSearchFilterPredicateWithColumnsDefinition(data, searchFilter);
    }
    return this.defaultSearchFilterPredicate(data, searchFilter);
  }

  /**
   * A predicate that compares the lower case filter with the lower case value of a row.
   * The combined textual value of a row is defined using the displayValue function of each Column definition.
   * @param data
   * @param filter
   * @private
   */
  private defaultSearchFilterPredicateWithColumnsDefinition<T>(data: T, filter: string): boolean {
    if (!filter) {
      return true;
    }
    const dataStr = this.tableFilterComponent.columns
      .reduce((currentTerm: string, column: Column) => {
        const valueAccessor: DisplayValueFn<T> | null = this.getValueAccessor<T>(column);
        if (!valueAccessor) {
          return currentTerm;
        }
        const textCellValue = valueAccessor(data);
        return currentTerm + this.getTextElement(textCellValue);
      }, '')
      .toLowerCase();

    const transformedFilter = filter.trim().toLowerCase();
    return dataStr.indexOf(transformedFilter) !== -1;
  }

  /**
   * A predicate that compares the lower case filter with the lower case value of a row.
   * The combined textual value of a row is defined by the property values of its data object.
   * @param data
   * @param filter
   * @private
   */
  private defaultSearchFilterPredicate<T>(data: T, filter: string): boolean {
    if (!filter) {
      return true;
    }
    const dataStr = Object.keys(data)
      .reduce((currentTerm, key) => {
        return currentTerm + this.getTextElement(data[key]);
      }, '')
      .toLowerCase();
    const transformedFilter = filter.trim().toLowerCase();
    return dataStr.indexOf(transformedFilter) !== -1;
  }

  /**
   * Use an obscure Unicode character to delimit the words in the concatenated string.
   * This avoids matches where the values of two columns combined will match the user's query
   * (e.g. `Flute` and `Stop` will match `Test`). The character is intended to be something
   * that has a very low chance of being typed in by somebody in a text field. This one in
   * particular is "White up-pointing triangle with dot" from
   * https://en.wikipedia.org/wiki/List_of_Unicode_characters
   * @param textElement
   * @private
   */
  private getTextElement(textElement: string | number | object): string {
    if (textElement && typeof textElement === 'object') {
      const arrTextElements = Object.values(<object>textElement);

      if (arrTextElements && arrTextElements.length > 0) {
        return arrTextElements.reduce((currentTerm, val) => {
          return `${currentTerm}${val}◬`;
        });
      }
    }
    return `${textElement}◬`;
  }

  private getValueAccessor<T>(column: Column): DisplayValueFn<T> {
    const { name } = column;
    if (!this._valueAccessor[name]) {
      this._valueAccessor[name] = this._getValueAccessor<T>(column);
    }
    return this._valueAccessor[name];
  }

  private _getValueAccessor<T>(column: Column): DisplayValueFn<T> {
    let displayValue = null;
    if ('displayValue' in column.table) {
      displayValue = column.table.displayValue;
    }
    if (!displayValue) {
      return null;
    }
    if (typeof displayValue === 'function') {
      return displayValue;
    }
    return (value: T): string | number => _get(value, displayValue);
  }
}
